/// <reference types="vite/client" />
/// <reference types="vite-plugin-pwa/client" />

import React, { useEffect, useState } from 'react'
import { Download, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt: () => Promise<void>
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

export const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isIOSInstalled =
      'standalone' in window.navigator &&
      (window.navigator as NavigatorWithStandalone).standalone === true
    setIsInstalled(isStandalone || isIOSInstalled)

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const beforeInstallPromptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(beforeInstallPromptEvent)
      setShowInstallPrompt(true)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setShowInstallPrompt(false)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      )
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setShowInstallPrompt(false)
    }

    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShowInstallPrompt(false)
    // Store dismissal in localStorage to avoid showing again for a while
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
  }

  // Don't show if already installed or dismissed recently
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) {
      const dismissedTime = parseInt(dismissed)
      const oneDayInMs = 24 * 60 * 60 * 1000
      if (Date.now() - dismissedTime < oneDayInMs) {
        setShowInstallPrompt(false)
      }
    }
  }, [])

  if (isInstalled || !showInstallPrompt || !deferredPrompt) {
    return null
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex items-center justify-between max-w-md mx-auto">
      <div className="flex items-center space-x-3">
        <div className="shrink-0">
          <img src="/favicon.svg" alt="MCC Donation" className="h-8 w-8" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">
            Install MCC Donation
          </p>
          <p className="text-xs text-gray-500">
            Add to your home screen for quick access
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Button size="sm" onClick={handleInstallClick} className="text-xs">
          <Download className="h-3 w-3 mr-1" />
          Install
        </Button>
        <Button size="sm" variant="ghost" onClick={handleDismiss}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}
